import { useState, useEffect } from 'react';
import { Download, FileText, TrendingUp, Building2, PieChart, Calendar, Loader2 } from 'lucide-react';
import { formatINR } from '@/utils/currency';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

const FY_OPTIONS = [
  { value: '2025-26', label: 'FY 2025-26' },
  { value: '2024-25', label: 'FY 2024-25' },
  { value: '2023-24', label: 'FY 2023-24' },
];

export default function InvestorReportsTab({ user }) {
  const [selectedFY, setSelectedFY] = useState('2025-26');
  const [investments, setInvestments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const investorId = user?.investorId || user?.id;
      const response = await api.get(`/investor/${investorId}/reports`);
      const data = response.data;
      setInvestments(data.investments || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const totalInvested = investments.reduce((sum, inv) => sum + (inv.invested || 0), 0);
  const totalCurrentValue = investments.reduce((sum, inv) => sum + (inv.currentValue || 0), 0);
  const totalReturns = totalCurrentValue - totalInvested;
  const avgROI = investments.length > 0
    ? (investments.reduce((sum, inv) => sum + (inv.returnsPct || 0), 0) / investments.length).toFixed(1)
    : 0;
  const activeCount = investments.filter(i => i.state !== 'COMPLETED' && i.state !== 'EXITED').length;

  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (type) => {
    const typeMap = {
      'Portfolio Statement': 'portfolio-statement',
      'Tax Report (Form 16A)': 'tax-report',
      'Transaction History (CSV)': 'transaction-history',
    };
    const slug = typeMap[type];
    if (!slug) return;

    setDownloading(type);
    try {
      const investorId = user?.investorId || user?.id;
      const response = await api.get(`/investor/${investorId}/reports/download/${slug}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${slug}_${investorId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} downloaded successfully`);
    } catch (err) {
      toast.error(`Failed to download ${type}`);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) return <div className="p-6 md:p-10"><DashboardSkeleton cards={4} rows={4} /></div>;

  return (
    <div className="p-6 md:p-10" data-testid="investor-reports">
      {error && <ErrorBanner message={error} onRetry={fetchReports} />}

      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-light text-white mb-2">Reports</h1>
          <p className="text-sm text-gray-500">Investment performance and tax documents</p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-gray-500" />
          <select
            value={selectedFY}
            onChange={(e) => setSelectedFY(e.target.value)}
            className="bg-white/5 border border-white/10 text-white px-4 py-2 text-sm"
            data-testid="fy-selector"
          >
            {FY_OPTIONS.map(fy => (
              <option key={fy.value} value={fy.value}>{fy.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10 mb-8">
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Total Invested</p>
          <p className="font-mono text-xl md:text-2xl font-light text-white" data-testid="total-invested">{formatINR(totalInvested)}</p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Current Value</p>
          <p className="font-mono text-xl md:text-2xl font-light text-[#4ADE80]" data-testid="current-value">{formatINR(totalCurrentValue)}</p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Total Returns</p>
          <p className="font-mono text-xl md:text-2xl font-light text-[#4ADE80]" data-testid="total-returns">+{formatINR(totalReturns)}</p>
        </div>
        <div className="bg-black px-4 md:px-6 py-5 md:py-6">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2 md:mb-3">Avg ROI</p>
          <p className="font-mono text-xl md:text-2xl font-light text-white" data-testid="avg-roi">{avgROI}%</p>
        </div>
      </div>

      {/* Download Reports Section */}
      <div className="border border-white/10 p-6 mb-8">
        <h2 className="text-lg text-white mb-4">Download Reports</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => handleDownload('Portfolio Statement')}
            disabled={downloading === 'Portfolio Statement'}
            className="flex items-center gap-3 p-4 border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/5 transition-all text-left disabled:opacity-50"
            data-testid="download-portfolio-statement"
          >
            <PieChart className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <div>
              <p className="text-sm text-white font-medium">Portfolio Statement</p>
              <p className="text-xs text-gray-500">Complete investment overview</p>
            </div>
            {downloading === 'Portfolio Statement' ? <Loader2 className="w-4 h-4 text-gray-500 ml-auto animate-spin" /> : <Download className="w-4 h-4 text-gray-500 ml-auto" />}
          </button>
          <button
            onClick={() => handleDownload('Tax Report (Form 16A)')}
            disabled={downloading === 'Tax Report (Form 16A)'}
            className="flex items-center gap-3 p-4 border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/5 transition-all text-left disabled:opacity-50"
            data-testid="download-tax-report"
          >
            <FileText className="w-5 h-5 text-green-400 flex-shrink-0" />
            <div>
              <p className="text-sm text-white font-medium">Tax Report (16A)</p>
              <p className="text-xs text-gray-500">TDS certificates for ITR</p>
            </div>
            {downloading === 'Tax Report (Form 16A)' ? <Loader2 className="w-4 h-4 text-gray-500 ml-auto animate-spin" /> : <Download className="w-4 h-4 text-gray-500 ml-auto" />}
          </button>
          <button
            onClick={() => handleDownload('Transaction History (CSV)')}
            disabled={downloading === 'Transaction History (CSV)'}
            className="flex items-center gap-3 p-4 border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/5 transition-all text-left disabled:opacity-50"
            data-testid="download-transaction-csv"
          >
            <TrendingUp className="w-5 h-5 text-purple-400 flex-shrink-0" />
            <div>
              <p className="text-sm text-white font-medium">Transaction History</p>
              <p className="text-xs text-gray-500">CSV download of all transactions</p>
            </div>
            {downloading === 'Transaction History (CSV)' ? <Loader2 className="w-4 h-4 text-gray-500 ml-auto animate-spin" /> : <Download className="w-4 h-4 text-gray-500 ml-auto" />}
          </button>
        </div>
      </div>

      {/* Investment Summary Table */}
      <div className="border border-white/10 mb-8">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg text-white">Investment Summary</h2>
          <span className="text-xs text-gray-500">{activeCount} active / {investments.length} total</span>
        </div>

        {investments.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Building2 className="w-8 h-8 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No investments found</p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 bg-white/5">
              <div className="col-span-3"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Property</p></div>
              <div className="col-span-1"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Type</p></div>
              <div className="col-span-2 text-right"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Invested</p></div>
              <div className="col-span-2 text-right"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Current Value</p></div>
              <div className="col-span-1 text-right"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Returns</p></div>
              <div className="col-span-2"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Date</p></div>
              <div className="col-span-1"><p className="text-[10px] text-gray-600 uppercase tracking-widest">Status</p></div>
            </div>

            {/* Table Rows */}
            {investments.map((inv) => (
              <div key={inv.investmentId} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`report-row-${inv.investmentId}`}>
                <div className="col-span-3">
                  <p className="text-sm text-white">{inv.projectName || 'Unknown'}</p>
                  <p className="text-xs text-gray-600">{inv.unitName || inv.unitId}</p>
                </div>
                <div className="col-span-1">
                  <p className="text-xs text-gray-400">{inv.propertyType || '-'}</p>
                </div>
                <div className="col-span-2 text-right">
                  <p className="font-mono text-sm text-white">{formatINR(inv.invested || 0)}</p>
                </div>
                <div className="col-span-2 text-right">
                  <p className="font-mono text-sm text-[#4ADE80]">{formatINR(inv.currentValue || 0)}</p>
                </div>
                <div className="col-span-1 text-right">
                  <p className="font-mono text-sm text-[#4ADE80]">+{(inv.returnsPct || 0).toFixed(1)}%</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-400">{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</p>
                </div>
                <div className="col-span-1">
                  <span className={`text-xs px-2 py-1 ${
                    inv.state === 'COMPLETED' || inv.state === 'EXITED' ? 'bg-blue-500/10 text-blue-400' : 'bg-[#4ADE80]/10 text-[#4ADE80]'
                  }`}>
                    {inv.state || 'Active'}
                  </span>
                </div>
              </div>
            ))}

            {/* Totals Row */}
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-white/5 border-t border-white/10">
              <div className="col-span-3">
                <p className="text-sm text-white font-medium">Total</p>
              </div>
              <div className="col-span-1" />
              <div className="col-span-2 text-right">
                <p className="font-mono text-sm text-white font-medium">{formatINR(totalInvested)}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="font-mono text-sm text-[#4ADE80] font-medium">{formatINR(totalCurrentValue)}</p>
              </div>
              <div className="col-span-1 text-right">
                <p className="font-mono text-sm text-[#4ADE80] font-medium">+{avgROI}%</p>
              </div>
              <div className="col-span-4" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
